import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@clay-run/glaze/button';
import { Text } from '@clay-run/glaze/text';

import { useAppBasePath } from '../../_shared/base-path';
import { useApi } from '../../_shared/hooks';
import { usePlaybooksLibrary } from '../use-playbooks-library';
import {
  currentUsageCardIndex,
  deleteSidebarBody,
  generateSessionForPending,
  isOriginUnderneath,
  originUrl,
  saveMenuOptions,
  usageCardNavigationUrl,
  usageCards,
  usageHeadline,
  type UsageCard,
} from '../usage-sidebar-logic';
import {
  chooseUsageSidebarAction,
  closeUsageSidebar,
  selectUsageSidebar,
  useUsageSidebarStore,
  type UsageSidebarDecision,
} from '../usage-sidebar-store';
import { reloadUsageSidebarAfterStale } from '../usage-sidebar-reload';
import { PushDrawer } from './PushDrawer';
import { SaveMenu } from './SaveMenu';
import { UsagePlayCards } from './UsagePlayCards';

/**
 * The persistent "Where is this used" sidebar (U1) — mounted ONCE in
 * `PlaybooksLayout` (a sibling of `<main data-playbooks-main>`), so it
 * survives every `/v2` route change. Renders nothing while
 * `usage-sidebar-store.ts`'s `pending` is `null`; every other render is
 * this one component whether the trigger was a save-gate or a delete.
 *
 * This component computes NO urls or copy of its own — `originUrl`,
 * `usageCardNavigationUrl`, `currentUsageCardIndex`, `isOriginUnderneath`,
 * and `sidebarCopy` all live in `usage-sidebar-logic.ts` (U1a, rewritten
 * per-RUNG and segment-led for QA round 8's U7, 2026-09-22) and are the
 * single source of truth both this component and its tests read from.
 *
 * What happens when a rep picks a footer button (actually writing the
 * save/delete) is the NEXT unit's `use-usage-sidebar-decision.ts` — this
 * component's buttons only ever call `chooseUsageSidebarAction`, which
 * just records the choice on the store; a decision-consuming hook mounted
 * at the origin (SequencesStep/SequenceCopyPane/PlaybooksConfigPage) does
 * the actual work and closes the sidebar on success.
 *
 * Plan 2026-09-28-008 U1 (rulebook R14) — a stale refusal
 * (`pending.staleRefusal`) turns this panel into the one place it lives: the
 * sentence as the subtitle, the last-known cards below it, and a single
 * "Reload to see their change" button in place of the Save menu. Nothing
 * reloads until the rep presses it.
 */
export function UsageSidebar() {
  const pending = useUsageSidebarStore(selectUsageSidebar);
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = useAppBasePath();
  // Plan 2026-09-28-008 U1 (R14) — only the stale state's Reload button uses
  // these; hooks run unconditionally, before the `pending === null` return.
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  // U6 (2026-09-17) — a play card's name needs the playbook name it falls
  // back to (`playDisplayName`); the library query is already cache-backed
  // (`PlayFlowShell`/Config hold it too), so this is a free read, not a new
  // network call on most navigations.
  const library = usePlaybooksLibrary();
  const playbookNameById = useMemo(
    () => new Map((library.data?.playbooks ?? []).map((p) => [p.id, p.name])),
    [library.data],
  );

  const places = pending?.places ?? [];
  const cards = useMemo(() => usageCards(places), [places]);

  if (pending === null) return null;

  const { mode, target, origin, status, originMissing, staleRefusal } = pending;
  const searchParams = new URLSearchParams(location.search);
  // Playbooks QA round 6, U3 (item 1, H2) — a SEGMENTATION target's own
  // triggering surface is Qualify, never Sequences; `linkStep` below already
  // made this exact distinction for the CARDS' own deep links, this just
  // reuses it for the origin-return link too.
  const linkStep = target.kind === 'sequence' ? 'sequences' : 'qualify';
  const underneath = isOriginUnderneath(origin, location.pathname, searchParams, linkStep);
  const currentIndex = currentUsageCardIndex(cards, origin);
  /** D2 — footer buttons AND the close X are disabled while a write is in flight. */
  const disabled = status === 'running';

  const goBackToOrigin = () => {
    navigate(originUrl(basePath, origin, linkStep));
  };

  /**
   * D2 — the close X has no `disabled` prop on `PushDrawer` (a generic
   * primitive shared by every A2 side panel, out of this bead's scope to
   * change), so this wraps `closeUsageSidebar` in a running-check instead:
   * functionally disabled (a click while running does nothing) even though
   * the X itself stays visually unchanged.
   *
   * D2 — inert while a write is running (see above).
   *
   * U3 (2026-09-17) / Codex fixes F2–F5 — for an "Apply to sequence" save
   * the X means "cancel the save, keep reviewing". It used to call
   * `openGenerateSession` right here and then navigate; the session opened
   * on the wrong page died in `PlayFlowShell`'s context-change clear, and
   * on the right page it opened under whatever rung/tab/row the rep had
   * since clicked to. Now it is a DECISION (`'resume-review'`), recorded
   * and returned-to exactly like "Update all": the ORIGIN consumes it
   * (`usage-sidebar-resume.ts`) once it is mounted, selected and settled —
   * `SequencesStep`'s D3 effect re-selects the origin rung, Config expands
   * the origin row, and `isOriginUnderneath` (step-aware) decides whether a
   * navigation is needed first.
   */
  const resumeReview = generateSessionForPending(pending);
  const handleClose = () => {
    if (disabled) return;
    // Plan 2026-09-28-008 U1 (R14) — a stale panel's X is a plain close: no
    // decision can be recorded against it (`chooseUsageSidebarAction` is a
    // no-op while stale), and the rep chose to walk away. Steps and filters
    // stay in their editors; a settings draft (held only by this pending) is
    // dropped.
    if (staleRefusal !== null) {
      closeUsageSidebar();
      return;
    }
    if (resumeReview !== null) {
      chooseAndReturn('resume-review', pending.requestId);
      return;
    }
    // QA round 8, U7 — for every other pending, X is a plain close. The
    // round-7 `'resume-edit'` branch is gone with the excursion that
    // motivated it: a card click no longer routes this tab (see
    // `handleNavigate`), so `PlayFlowShell`'s context-change clear can never
    // take the rep's typed text out from under an open sidebar.
    closeUsageSidebar();
  };

  /**
   * QA round 8, U7 — a card opens its rung in a NEW TAB rather than
   * navigating this one. `basePath` is mount-relative (`useAppBasePath()`),
   * which `window.open` resolves against the current origin — same origin,
   * same session, same auth. `'noopener'` is the standard hardening for a
   * `_blank` target; nothing here needs a handle on the opened window.
   *
   * The consequence this round is built around: the page underneath never
   * moves, so the rep's unsaved editor state stays exactly where it was, no
   * navigation is issued (so `UnsavedWorkGuard` is never tripped), and the
   * sidebar's pending survives untouched.
   */
  const handleNavigate = (card: UsageCard) => {
    window.open(usageCardNavigationUrl(basePath, card, linkStep), '_blank', 'noopener');
  };

  /**
   * Plan 2026-09-28-007 U6 (rulebook R9, Codex c3-1) — the choice names the
   * request it was made for, so a click that lands after a newer save
   * replaced this one records nothing (and navigates nowhere).
   */
  const chooseAndReturn = (chosen: NonNullable<UsageSidebarDecision>, requestId: string) => {
    if (useUsageSidebarStore.getState().pending?.requestId !== requestId) return;
    chooseUsageSidebarAction(chosen, requestId);
    if (!underneath) goBackToOrigin();
  };

  const title = mode === 'save' ? `Save changes to "${target.name}"?` : `Delete "${target.name}"?`;
  // Plan 2026-09-28-006 U7 / 2026-09-28-007 U9 (rulebook R4, R6) — the
  // subtitle is list-matching in BOTH modes, derived from the same `cards`
  // the sidebar renders below it: {@link usageHeadline} in save mode, and
  // {@link deleteSidebarBody} in delete mode (replacing `sidebarCopy`, which
  // counted places/segments rather than cards — save mode no longer reads it
  // at all, the two-button "Update all"/"Save as new" framing it implied is
  // gone, replaced by {@link saveMenuOptions}/{@link SaveMenu}).
  // Plan 2026-09-28-008 U1 (R14) — a stale refusal's sentence replaces the
  // headline: it is the one thing the rep needs to read before acting.
  const subtitle =
    staleRefusal !== null
      ? staleRefusal
      : mode === 'save'
        ? usageHeadline(cards, target.kind)
        : deleteSidebarBody(cards, target.kind);

  /**
   * D17 (Codex NEW-RUNG-DELETED-WHILE-PENDING) — the origin (rung/library
   * row) this request was opened for no longer exists. Nothing was ever
   * written for this decision (`SequencesStep.tsx`'s reselect effect caught
   * it before the decision hook could take it), so there is nothing left
   * to offer except closing: no footer at all, only the header's X, which
   * is enabled here (`disabled` is `status === 'running'`, and
   * `markUsageSidebarOriginMissing` already reset `status` to `'idle'`).
   */
  if (originMissing) {
    return (
      <PushDrawer title={title} onClose={closeUsageSidebar}>
        <Text size="sm" variant="tertiary">
          This segment was removed — nothing was saved.
        </Text>
      </PushDrawer>
    );
  }

  /**
   * Plan 2026-09-28-008 U1 (rulebook R14) — while stale the footer is ONE
   * action, "Reload to see their change" (`usage-sidebar-reload.ts`): the
   * Save menu is hidden (any choice would be refused again for the same
   * reason). The helper never throws; it re-arms this same request, or keeps
   * it stale and toasts. Disabled while it runs (`status: 'running'`).
   */
  const footer =
    staleRefusal !== null ? (
      <Button
        variant="primary"
        disabled={disabled}
        onClick={() => void reloadUsageSidebarAfterStale(apiFetch, queryClient, pending.requestId)}
      >
        Reload to see their change
      </Button>
    ) : mode === 'save' ? (
      // Plan 2026-09-28-007 U6 (rulebook R9, Codex c3-1) — keyed on the
      // request: a replaced pending unmounts the menu and any ③ confirm it
      // had open, so a confirm opened for one save can never authorise the
      // next one.
      <SaveMenu
        key={pending.requestId}
        label="Save"
        options={saveMenuOptions(pending)}
        disabled={disabled}
        onChoose={(decision) => chooseAndReturn(decision, pending.requestId)}
      />
    ) : (
      <Button variant="danger" disabled={disabled} onClick={() => chooseAndReturn('delete', pending.requestId)}>
        Delete
      </Button>
    );

  return (
    <PushDrawer title={title} subtitle={subtitle} onClose={handleClose} footer={footer}>
      <UsagePlayCards cards={cards} currentIndex={currentIndex} playbookNameById={playbookNameById} onNavigate={handleNavigate} />
      {resumeReview !== null && !disabled && staleRefusal === null && (
        <div>
          <Button variant="inline-text" size="xs" onClick={handleClose}>
            Keep reviewing instead
          </Button>
        </div>
      )}
      {!underneath && (
        <div>
          {/* QA round 8, U7 — a plain navigate back to the origin. It is kept
              (and still gated on `!underneath`) because a rep can still rail-
              click away with the sidebar open; that MANUAL navigation is
              `UnsavedWorkGuard`'s job, not this sidebar's. */}
          <Button variant="inline-text" size="xs" onClick={goBackToOrigin}>
            {mode === 'save' ? 'Back to editing' : 'Back'}
          </Button>
        </div>
      )}
    </PushDrawer>
  );
}
